import { Box, Flex } from '@chakra-ui/react'
import { DirectiveDescriptor, insertMarkdown$, usePublisher } from '@mdxeditor/editor'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { getDocById } from 'utils'
import ManageTextDrawer from '../../application-explorer/src/components/textContent/ManageTextDrawer'
import TextContentList from '../../application-explorer/src/components/textContent/TextContentList'
import {
  ContentRecord,
  getContent,
  useTextContent,
} from '../../application-explorer/src/utils/contentUtils'
import Dialog from '../Dialog'
import IconButton from '../IconButton'
import { DocumentTextIcon } from '../icons'
import Text from '../Text'
import Tooltip from '../Tooltip'
import MarkdownEditor from './MarkdownEditor'

export const TextDirectiveDescriptor: DirectiveDescriptor = {
  name: 'content',
  type: 'textDirective',
  testNode(node) {
    return node.name === 'content'
  },
  attributes: ['id'],
  hasChildren: false,
  Editor: props => {
    const [openEdit, setOpenEdit] = useState(false)
    const contentId = props.mdastNode.attributes?.id || ''

    const { data: document } = useQuery({
      queryKey: ['content', contentId],
      queryFn: () => getDocById('content', contentId),
      enabled: !!contentId,
    })

    let text = 'Loading content...'
    if (document === null) text = 'Content not found'
    if (document) text = document.value?.en

    return (
      <>
        <Tooltip positioning={{ placement: 'top' }} content={document?.title}>
          <Box
            display="inline-block"
            position="relative"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="xs"
            p={1}
            cursor="pointer"
            onClick={() => document && setOpenEdit(true)}
            _hover={{
              borderColor: 'gray.400',
              bg: 'gray.50',
            }}
          >
            <MarkdownEditor readOnly lineClamp={4} value={text} />
          </Box>
        </Tooltip>

        <ManageTextDrawer
          id={openEdit ? contentId : undefined}
          open={openEdit}
          onDone={() => setOpenEdit(false)}
        />
      </>
    )
  },
}

export function CreateText() {
  const insertMarkdown = usePublisher(insertMarkdown$)
  const { content, status } = useTextContent()
  const [open, setOpen] = useState(false)
  const [expandedContentId, setExpandedContentId] = useState<string>()
  const [childDocs, setChildDocs] = useState<ContentRecord[]>([])

  const expandedContent = content?.find(c => c.id === expandedContentId)

  useEffect(() => {
    if (open) return

    setExpandedContentId(undefined)
    setChildDocs([])
  }, [open])

  useEffect(() => {
    setChildDocs([])
    if (!expandedContent) return

    const { id, workspaceId } = expandedContent
    getContent([workspaceId], id).then(setChildDocs)
  }, [content, expandedContentId])

  function handleContentSelect(contentId: string) {
    insertMarkdown(`:content{id=${contentId}}`)
    setOpen(false)
  }

  return (
    <Dialog
      lazyMount
      open={open}
      size="xl"
      onClose={() => setOpen(false)}
      title="Select Text Content"
      trigger={
        <IconButton
          aria-label="Select Text Content"
          variant="ghost"
          icon={<DocumentTextIcon size="sm" type="outline" />}
          size="sm"
          borderRadius="xs"
          onClick={() => setOpen(true)}
        />
      }
    >
      <Flex direction="column" gap={2} p={4}>
        {expandedContentId && (
          <Flex justify={'center'} gap={1}>
            <Text textStyle={'bodySmall'} opacity={0.7} color={'rgba(23, 23, 23, 0.6)'}>
              Sub content of
            </Text>
            <Text
              textStyle={'bodySmall'}
              opacity={0.7}
              color={'rgba(23, 23, 23, 0.6)'}
              as="u"
              cursor={'pointer'}
              onClick={() => setExpandedContentId(undefined)}
            >
              {expandedContent?.title}
            </Text>
          </Flex>
        )}
        <TextContentList
          content={expandedContentId ? childDocs : content}
          onChildrenSelect={setExpandedContentId}
          onSelect={handleContentSelect}
          status={status}
        />
      </Flex>
    </Dialog>
  )
}
